import { getAuthToken } from '@/utils/auth';
import { apiUrl } from '@/config/api';
import { loadRazorpaySdk } from '@/utils/payments/razorpay-new';
import { waitForEntitlement } from '@/utils/subscription';
import { fetchCheckoutReadiness } from '@/utils/payments/checkoutReadiness';

async function assertUsdCheckoutOpen() {
  const readiness = await fetchCheckoutReadiness();
  if (!readiness.usdCheckoutAvailable) {
    throw new Error(readiness.message || 'US card checkout is not available yet.');
  }
}

function authHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function readApiError(errorData, fallback) {
  const detail = errorData?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const first = detail.find((item) => item?.msg || item?.message);
    if (first?.msg) return first.msg;
    if (first?.message) return first.message;
  }
  return fallback;
}

function checkoutPhone(user) {
  const raw = String(user?.phone || user?.mobile || '').replace(/\D/g, '');
  if (!raw || /^0+$/.test(raw) || raw.length < 8) return '';
  return raw;
}

async function fetchPublicKey() {
  try {
    const response = await fetch(apiUrl('/api/payments/razorpay/key'), { credentials: 'include' });
    if (!response.ok) return '';
    const data = await response.json();
    return data.keyId || data.key_id || '';
  } catch {
    return '';
  }
}

export async function createSubscriptionOrder(planType) {
  const response = await fetch(apiUrl('/api/payments/subscription/create-order'), {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify({ plan_type: planType }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(readApiError(errorData, 'Could not start checkout. Please try again.'));
  }
  return response.json();
}

async function verifySubscription(payload) {
  const response = await fetch(apiUrl('/api/payments/subscription/verify'), {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(readApiError(errorData, 'We could not confirm this payment. Please try again.'));
  }
  return response.json();
}

/**
 * Open Razorpay for a server-priced plan.
 * Access is granted as soon as the checkout signature verifies.
 */
export async function startSubscriptionCheckout({
  planType = 'monthly',
  user,
  onSuccess,
  onDismiss,
  onFail,
}) {
  try {
    await assertUsdCheckoutOpen();
    await loadRazorpaySdk();
    if (!window.Razorpay) throw new Error('Card checkout could not load. Refresh and try once more.');

    const order = await createSubscriptionOrder(planType);
    const keyId = order.keyId || process.env.REACT_APP_RAZORPAY_KEY_ID || (await fetchPublicKey());
    if (!keyId) throw new Error('Checkout is briefly unavailable. Please try again in a minute.');

    const rzp = new window.Razorpay({
      key: keyId,
      amount: order.amount,
      currency: order.currency || 'USD',
      name: 'ByOnco',
      description: order.description || 'ByOnco PRO',
      order_id: order.orderId,
      prefill: {
        name: user?.full_name || user?.name || '',
        email: user?.email || '',
        contact: checkoutPhone(user),
      },
      notes: { plan_type: planType },
      theme: { color: '#0f3e17' },
      handler: async (response) => {
        try {
          const verified = await verifySubscription({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          let entitlement = verified?.subscription || null;
          if (verified?.active && entitlement) {
            if (onSuccess) onSuccess({ pending: false, entitlement, verified });
            return;
          }
          if (user) {
            entitlement = await waitForEntitlement(user, { attempts: 4, delayMs: 400 });
          }
          if (onSuccess) onSuccess({ pending: !entitlement, entitlement, verified });
        } catch (error) {
          if (onFail) onFail(error);
        }
      },
      modal: {
        ondismiss: () => {
          if (onDismiss) onDismiss();
        },
      },
    });

    rzp.on('payment.failed', (response) => {
      const reason = response.error?.description || response.error?.reason;
      if (onFail) onFail(new Error(reason || 'The card was declined. Try another card, or email care@getbyonco.com.'));
    });
    rzp.open();
  } catch (error) {
    if (onFail) onFail(error);
    else throw error;
  }
}

/**
 * Pay-first teleconsultation checkout. Does not create an appointment.
 * Caller must book only after onPaid receives verified Razorpay ids.
 */
export async function startTeleconsultationCheckout({
  user,
  prefill = {},
  onPaid,
  onDismiss,
  onFail,
}) {
  try {
    await assertUsdCheckoutOpen();
    await loadRazorpaySdk();
    if (!window.Razorpay) throw new Error('Card checkout could not load. Refresh and try once more.');

    const order = await createSubscriptionOrder('teleconsultation');
    const keyId = order.keyId || process.env.REACT_APP_RAZORPAY_KEY_ID || (await fetchPublicKey());
    if (!keyId) throw new Error('Checkout is briefly unavailable. Please try again in a minute.');

    const rzp = new window.Razorpay({
      key: keyId,
      amount: order.amount,
      currency: order.currency || 'INR',
      name: 'ByOnco',
      description: order.description || 'Oncologist teleconsultation',
      order_id: order.orderId,
      prefill: {
        name: prefill.name || user?.full_name || user?.name || '',
        email: prefill.email || user?.email || '',
        contact: prefill.contact || checkoutPhone(user),
      },
      notes: { plan_type: 'teleconsultation' },
      theme: { color: '#0f3e17' },
      handler: async (response) => {
        try {
          await verifySubscription({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          if (onPaid) {
            await onPaid({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
          }
        } catch (error) {
          if (onFail) onFail(error);
        }
      },
      modal: {
        ondismiss: () => {
          if (onDismiss) onDismiss();
        },
      },
    });

    rzp.on('payment.failed', (response) => {
      const reason = response.error?.description || response.error?.reason;
      if (onFail) onFail(new Error(reason || 'Payment failed. No appointment was booked.'));
    });
    rzp.open();
  } catch (error) {
    if (onFail) onFail(error);
    else throw error;
  }
}
